// v10/src/components/MyProfile.js
// v1.1 — LanguageSwitcher ahora sincroniza el idioma con el backend (PATCH /api/users/me/language)
// v1.2 — A6.2 parte 2B — Sección "Notificaciones" (preferencias multi-nivel)
import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Save, Loader, CheckCircle, AlertCircle, Fingerprint, Bell, FileDown, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import { notificationPreferencesService } from '../services/notificationPreferencesService';
import { useToast } from '../hooks/useToast';
import ExportReportModal from './common/ExportReportModal';
import { reportsService } from '../services/reportsService';

// ── Helpers ───────────────────────────────────────────────────────
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
};

const ExpiryBadge = ({ dateStr, label }) => {
  const { t, i18n } = useTranslation();
  if (!dateStr) return null;
  const days = daysUntil(dateStr);
  const date = new Date(dateStr).toLocaleDateString(
    i18n.language === 'en' ? 'en-US' : 'es-AR'
  );
  if (days < 0)  return <span style={{ fontSize:11, fontWeight:700, color:'#ef4444', background:'rgba(239,68,68,0.1)', padding:'2px 8px', borderRadius:20 }}>⚠️ {label} {t('expiry_expired')}</span>;
  if (days < 30) return <span style={{ fontSize:11, fontWeight:700, color:'#f59e0b', background:'rgba(245,158,11,0.1)', padding:'2px 8px', borderRadius:20 }}>⏰ {t('expiry_days', { count: days, date })}</span>;
  return <span style={{ fontSize:11, fontWeight:700, color:'#16a34a', background:'rgba(22,163,74,0.1)', padding:'2px 8px', borderRadius:20 }}>✅ {t('expiry_valid', { date })}</span>;
};

// ── Language Switcher ─────────────────────────────────────────────
const LANGUAGES = [
  { code: 'es-AR', label: 'ES-AR', flag: '🇦🇷' }, 
  { code: 'en',    label: 'EN',    flag: '🇺🇸' },
];

const LanguageSwitcher = ({ darkMode }) => {
  const { i18n, t } = useTranslation();
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const handleSelect = async (code) => {
    // Si ya es el idioma actual, no hacer nada
    if (code === i18n.language) return;

    // 1. Cambio inmediato en UI + persistencia local (no esperamos al backend)
    i18n.changeLanguage(code);
    localStorage.setItem('condor360_language', code);

    // 2. Sincronizar con backend para que los emails lleguen en el idioma correcto.
    //    Si falla (ej: sin red), no rompemos la UX — el cambio local ya se aplicó.
    try {
      await api.patch('/api/users/me/language', { preferred_language: code });
    } catch (err) {
      console.warn('[LanguageSwitcher] No se pudo sincronizar idioma con backend:', err.message);
    }
  };

  const text  = darkMode ? 'white'                 : '#0f172a';
  const sub   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';

  return (
    <div style={{ borderRadius:18, background:cardBg, border, padding:'20px' }}>
      <p style={{ margin:'0 0 14px', fontSize:11, fontWeight:800, textTransform:'uppercase',
        letterSpacing:'0.1em', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8' }}>
        {t('language_section')}
      </p>
      <div style={{ display:'flex', gap:8 }}>
        {LANGUAGES.map(lang => (
          <motion.button
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: 12,
              border: i18n.language === lang.code
                ? '2px solid #2563eb'
                : darkMode ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid #e2e8f0',
              background: i18n.language === lang.code
                ? darkMode ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.07)'
                : darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            }}>
            <span style={{ fontSize: 20 }}>{lang.flag}</span>
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: i18n.language === lang.code ? '#2563eb' : sub,
            }}>{lang.label}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// ── Sub-components — definidos FUERA para evitar remount en cada render ──
const SectionTitle = ({ children, darkMode }) => (
  <p style={{ margin:'0 0 14px', fontSize:11, fontWeight:800, textTransform:'uppercase',
    letterSpacing:'0.1em', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8' }}>
    {children}
  </p>
);

const Field = ({ label, children, badge, sub }) => (
  <div style={{ marginBottom:16 }}>
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:7 }}>
      <label style={{ fontSize:12, fontWeight:700, color:sub, letterSpacing:'0.04em' }}>{label}</label>
      {badge}
    </div>
    {children}
  </div>
);

// ── Notification preferences section (A6.2 parte 2B) ─────────────
const ToggleRow = ({ label, helper, checked, onChange, darkMode, sub, text }) => (
  <label
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0', cursor: 'pointer',
    }}
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={onChange}
      style={{ marginTop: 3, width: 18, height: 18, cursor: 'pointer', accentColor: '#2563eb' }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: text }}>{label}</p>
      {helper && (
        <p style={{ margin: '2px 0 0', fontSize: 11, color: sub, lineHeight: 1.4 }}>{helper}</p>
      )}
    </div>
  </label>
);

const RadioRow = ({ label, value, current, onChange, darkMode, sub, text }) => {
  const selected = current === value;
  return (
    <label
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 12, cursor: 'pointer',
        border: selected
          ? '1.5px solid #2563eb'
          : (darkMode ? '1.5px solid rgba(255,255,255,0.08)' : '1.5px solid #e2e8f0'),
        background: selected
          ? (darkMode ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.06)')
          : 'transparent',
        marginBottom: 6, transition: 'all 0.15s',
      }}
    >
      <input
        type="radio"
        checked={selected}
        onChange={() => onChange(value)}
        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#2563eb' }}
      />
      <span style={{ fontSize: 13, color: selected ? (darkMode ? 'white' : '#0f172a') : (darkMode ? 'rgba(255,255,255,0.7)' : '#475569'), fontWeight: selected ? 600 : 500 }}>
        {label}
      </span>
    </label>
  );
};

const NotificationPreferencesSection = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [prefs, setPrefs]       = useState(null);
  const [isDefault, setIsDef]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [loadError, setLoadErr] = useState('');

  const text   = darkMode ? 'white'                    : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)'    : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)'   : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const subBg  = darkMode ? 'rgba(255,255,255,0.02)'   : '#f8fafc';
  const faint  = darkMode ? 'rgba(255,255,255,0.3)'    : '#94a3b8';

  const load = async () => {
    setLoading(true); setLoadErr('');
    try {
      const res  = await notificationPreferencesService.getMy();
      const data = res?.data?.data || {};
      setPrefs({
        email_daily_summary:     data.email_daily_summary     !== false,
        push_threshold_crossed:  data.push_threshold_crossed  !== false,
        push_aog_alerts:         data.push_aog_alerts         !== false,
        email_aog_alerts:        data.email_aog_alerts        !== false,
        min_threshold_to_notify: data.min_threshold_to_notify || 'next',
      });
      setIsDef(!!data.is_default);
    } catch (_err) {
      setLoadErr(t('notification_prefs_load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  const setThreshold = (v)  => setPrefs((p) => ({ ...p, min_threshold_to_notify: v }));

  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const res = await notificationPreferencesService.updateMy(prefs);
      const data = res?.data?.data || {};
      setPrefs({
        email_daily_summary:     data.email_daily_summary     !== false,
        push_threshold_crossed:  data.push_threshold_crossed  !== false,
        push_aog_alerts:         data.push_aog_alerts         !== false,
        email_aog_alerts:        data.email_aog_alerts        !== false,
        min_threshold_to_notify: data.min_threshold_to_notify || 'next',
      });
      setIsDef(false);
      toast.success(t('notification_prefs_saved'));
    } catch (_err) {
      toast.error(t('notification_prefs_save_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderRadius: 18, background: cardBg, border, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 10,
          background: 'rgba(37,99,235,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Bell style={{ width: 16, height: 16, color: '#2563eb' }} />
        </div>
        <SectionTitle darkMode={darkMode}>{t('notification_prefs_title')}</SectionTitle>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', color: sub, fontSize: 13 }}>
          <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
          {t('notification_prefs_loading')}
        </div>
      )}

      {!loading && loadError && (
        <div style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#ef4444' }}>{loadError}</p>
          <button
            type="button"
            onClick={load}
            style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {t('notification_prefs_retry')}
          </button>
        </div>
      )}

      {!loading && !loadError && prefs && (
        <>
          {isDefault && (
            <div style={{
              padding: '10px 12px', borderRadius: 10,
              background: darkMode ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)',
              border: '1px solid rgba(59,130,246,0.2)',
              marginBottom: 14,
            }}>
              <p style={{ margin: 0, fontSize: 12, color: '#2563eb', lineHeight: 1.5 }}>
                {t('notification_prefs_default_notice')}
              </p>
            </div>
          )}

          {/* Email */}
          <div style={{ padding: '12px 14px', borderRadius: 12, background: subBg, marginBottom: 12 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: faint }}>
              {t('notification_prefs_email_subsection')}
            </p>
            <ToggleRow
              label={t('notification_prefs_email_daily_summary')}
              helper={t('notification_prefs_email_daily_summary_helper')}
              checked={prefs.email_daily_summary}
              onChange={() => toggle('email_daily_summary')}
              darkMode={darkMode} sub={sub} text={text}
            />
            <ToggleRow
              label={t('notification_prefs_email_aog_alerts')}
              checked={prefs.email_aog_alerts}
              onChange={() => toggle('email_aog_alerts')}
              darkMode={darkMode} sub={sub} text={text}
            />
          </div>

          {/* Push */}
          <div style={{ padding: '12px 14px', borderRadius: 12, background: subBg, marginBottom: 12 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: faint }}>
              {t('notification_prefs_push_subsection')}
            </p>
            <ToggleRow
              label={t('notification_prefs_push_threshold_crossed')}
              checked={prefs.push_threshold_crossed}
              onChange={() => toggle('push_threshold_crossed')}
              darkMode={darkMode} sub={sub} text={text}
            />
            <ToggleRow
              label={t('notification_prefs_push_aog_alerts')}
              checked={prefs.push_aog_alerts}
              onChange={() => toggle('push_aog_alerts')}
              darkMode={darkMode} sub={sub} text={text}
            />
          </div>

          {/* Threshold */}
          <div style={{ padding: '12px 14px', borderRadius: 12, background: subBg, marginBottom: 16 }}>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: faint }}>
              {t('notification_prefs_threshold_subsection')}
            </p>
            <RadioRow label={t('notification_prefs_threshold_next')}     value="next"     current={prefs.min_threshold_to_notify} onChange={setThreshold} darkMode={darkMode} sub={sub} text={text} />
            <RadioRow label={t('notification_prefs_threshold_critical')} value="critical" current={prefs.min_threshold_to_notify} onChange={setThreshold} darkMode={darkMode} sub={sub} text={text} />
            <RadioRow label={t('notification_prefs_threshold_expired')}  value="expired"  current={prefs.min_threshold_to_notify} onChange={setThreshold} darkMode={darkMode} sub={sub} text={text} />
          </div>

          <motion.button
            type="button"
            onClick={handleSave}
            disabled={saving}
            whileHover={!saving ? { scale: 1.02 } : {}}
            whileTap={!saving ? { scale: 0.97 } : {}}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 12, border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              color: 'white',
              background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving
              ? <><Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> {t('notification_prefs_saving')}</>
              : <><Save style={{ width: 14, height: 14 }} /> {t('notification_prefs_save')}</>
            }
          </motion.button>
        </>
      )}
    </div>
  );
};

// ── MyProfile ─────────────────────────────────────────────────────
const MyProfile = ({ currentUser, darkMode, onBack, onUserUpdated }) => {
  const { t, i18n } = useTranslation();
  // Namespace 'admin' para las claves de reportes (compartidas con el panel admin).
  const { t: tAdmin } = useTranslation('admin');
  const [logbookModalOpen, setLogbookModalOpen] = useState(false);
  const [form, setForm] = useState({
    name:            currentUser?.name            || '',
    phone:           currentUser?.phone            || '',
    license_number:  currentUser?.license_number  || '',
    license_expiry:  currentUser?.license_expiry  ? currentUser.license_expiry.split('T')[0] : '',
    medical_expiry:  currentUser?.medical_expiry  ? currentUser.medical_expiry.split('T')[0] : '',
  });
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');
  const [biometricEnabled, setBiometricEnabled] = useState(
    !!localStorage.getItem('condor360_webauthn_registered')
  );

  const text   = darkMode ? 'white'                    : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)'    : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)'   : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  // ✅ Helper — inputs controlados
  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const inp = useMemo(() => ({
    width: '100%', padding: '11px 14px',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border: darkMode ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid #e2e8f0',
    borderRadius: 12, fontSize: 14, color: text,
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  }), [darkMode, text]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError(t('error_name_required')); return; }
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await api.put('/api/users/me', {
        name:           form.name.trim(),
        phone:          form.phone.trim() || null,
        license_number: form.license_number.trim() || null,
        license_expiry: form.license_expiry || null,
        medical_expiry: form.medical_expiry || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUserUpdated) onUserUpdated(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || t('error_saving'));
    } finally { setSaving(false); }
  };

  const handleDisableBiometric = () => {
    localStorage.removeItem('condor360_webauthn_registered');
    localStorage.removeItem('condor360_webauthn_email');
    localStorage.removeItem('condor360_biometric_dismissed');
    setBiometricEnabled(false);
  };

  const handleEnableBiometric = async () => {
    if (!window.PublicKeyCredential) {
      setError(t('error_biometric_not_supported'));
      return;
    }
    try {
      const stored = localStorage.getItem('condor360_current_user') || sessionStorage.getItem('condor360_current_user');
      const u = stored ? JSON.parse(stored) : null;
      if (!u?.token) { setError(t('error_biometric_not_logged')); return; }
      localStorage.removeItem('condor360_biometric_dismissed');
      localStorage.setItem('condor360_webauthn_email', currentUser?.email || '');
      setBiometricEnabled(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError(t('error_biometric_failed'));
    }
  };

  const getRoleLabel = (role) => {
    if (role === 'pilot')      return t('role_pilot');
    if (role === 'instructor') return t('role_instructor');
    return t('role_admin');
  };



  return (
    <motion.div
      initial={{ opacity:0, x:40 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:40 }}
      transition={{ type:'spring', stiffness:160, damping:20 }}
      style={{ minHeight:'100vh', background: darkMode?'#0a0f1e':'#f8fafc', overflowY:'auto' }}>

      {/* Header */}
      <div style={{ position:'sticky', top:0, zIndex:10, background: darkMode?'rgba(10,15,30,0.95)':'rgba(248,250,252,0.95)', backdropFilter:'blur(20px)', borderBottom: border, padding:'16px 20px', display:'flex', alignItems:'center', gap:12 }}>
        <motion.button onClick={onBack} whileTap={{ scale:0.95 }}
          style={{ width:38, height:38, borderRadius:11, border:border, background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:sub }}>
          <ArrowLeft style={{ width:18, height:18 }} />
        </motion.button>
        <div>
          <p style={{ margin:0, fontSize:17, fontWeight:800, color:text }}>{t('profile_title')}</p>
          <p style={{ margin:0, fontSize:12, color:sub }}>{currentUser?.email}</p>
        </div>
        <motion.button onClick={handleSave} disabled={saving}
          whileHover={!saving?{scale:1.03}:{}} whileTap={!saving?{scale:0.97}:{}}
          style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:7, padding:'9px 18px', borderRadius:12, border:'none', cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, color:'white', background:saved?'#16a34a':'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
          {saving ? <Loader style={{ width:14, height:14, animation:'spin 1s linear infinite' }} />
           : saved ? <CheckCircle style={{ width:14, height:14 }} />
           : <Save style={{ width:14, height:14 }} />}
          {saving ? t('profile_saving') : saved ? t('profile_saved') : t('profile_save')}
        </motion.button>
      </div>

      <div style={{ maxWidth:520, margin:'0 auto', padding:'24px 20px', display:'flex', flexDirection:'column', gap:20 }}>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderRadius:14, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)' }}>
              <AlertCircle style={{ width:16, height:16, color:'#ef4444', flexShrink:0 }} />
              <p style={{ margin:0, fontSize:13, color:'#ef4444' }}>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Avatar + rol */}
        <div style={{ display:'flex', alignItems:'center', gap:16, padding:'20px', borderRadius:18, background:cardBg, border }}>
          <div style={{ width:60, height:60, borderRadius:18, background:'linear-gradient(135deg,#2563eb,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, fontWeight:800, color:'white', flexShrink:0 }}>
            {form.name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div>
            <p style={{ margin:0, fontSize:18, fontWeight:800, color:text }}>{form.name || '—'}</p>
            <p style={{ margin:'3px 0 0', fontSize:13, color:sub }}>
              {getRoleLabel(currentUser?.role)}
              {currentUser?.organization_name && ` · ${currentUser.organization_name}`}
            </p>
          </div>
        </div>

        {/* Datos personales */}
        <div style={{ borderRadius:18, background:cardBg, border, padding:'20px' }}>
          <SectionTitle darkMode={darkMode}>{t('personal_data')}</SectionTitle>
          <Field label={t('full_name')}>
            <input
              value={form.name}
              onBlur={e=>setForm(f=>({...f,name:e.target.value}))}
              style={inp} placeholder={t('full_name')} />
          </Field>
          <Field label={t('email_readonly')}>
            <input value={currentUser?.email || ''} disabled
              style={{ ...inp, opacity:0.45, cursor:'not-allowed' }} />
          </Field>
          <Field label={t('phone_label')}>
            <input
              value={form.phone}
              onBlur={e=>setForm(f=>({...f,phone:e.target.value}))}
              style={inp} placeholder="Ej: +5491123456789"
              type="tel" />
          </Field>
        </div>

        {/* Documentación aeronáutica */}
        <div style={{ borderRadius:18, background:cardBg, border, padding:'20px' }}>
          <SectionTitle darkMode={darkMode}>{t('aeronautical_docs')}</SectionTitle>

          <Field label={t('license_number_label')}>
            <input
              value={form.license_number}
              onBlur={e=>setForm(f=>({...f,license_number:e.target.value}))}
              style={inp} placeholder="Ej: 12345" />
          </Field>

          <Field label={t('license_expiry_label')}
            badge={<ExpiryBadge dateStr={form.license_expiry} label={t('license_expiry_label')} />}>
            <input type="date" value={form.license_expiry} onChange={e=>setForm(f=>({...f,license_expiry:e.target.value}))}
              style={inp}
              onFocus={e=>e.target.style.borderColor='rgba(37,99,235,0.5)'}
              onBlur={e=>e.target.style.borderColor=darkMode?'rgba(255,255,255,0.1)':'#e2e8f0'} />
          </Field>

          <Field label={t('medical_expiry_label')}
            badge={<ExpiryBadge dateStr={form.medical_expiry} label={t('medical_expiry_label')} />}>
            <input type="date" value={form.medical_expiry} onChange={e=>setForm(f=>({...f,medical_expiry:e.target.value}))}
              style={inp}
              onFocus={e=>e.target.style.borderColor='rgba(37,99,235,0.5)'}
              onBlur={e=>e.target.style.borderColor=darkMode?'rgba(255,255,255,0.1)':'#e2e8f0'} />
          </Field>

          <p style={{ margin:'4px 0 0', fontSize:11, color:sub }}>
            {t('docs_admin_note')}
          </p>
        </div>

        {/* Mi Logbook — disponible para todos los roles operativos */}
        <div style={{ borderRadius:18, background:cardBg, border, padding:'20px' }}>
          <SectionTitle darkMode={darkMode}>{tAdmin('report_my_logbook_title')}</SectionTitle>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{
              width:44, height:44, borderRadius:14, flexShrink:0,
              background: darkMode ? 'rgba(14,165,233,0.12)' : 'rgba(14,165,233,0.08)',
              border: '1px solid rgba(14,165,233,0.25)',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              <BookOpen style={{ width:22, height:22, color:'#0ea5e9' }} />
            </div>
            <p style={{ margin:0, fontSize:13, color:sub, flex:1, lineHeight:1.4 }}>
              {tAdmin('report_my_logbook_description')}
            </p>
          </div>
          <motion.button
            onClick={() => setLogbookModalOpen(true)}
            whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
            style={{
              marginTop:14, width:'100%', display:'flex', alignItems:'center',
              justifyContent:'center', gap:8, padding:'12px 16px', borderRadius:12,
              border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700,
              color:'white', background:'linear-gradient(135deg,#0ea5e9,#0369a1)',
              boxShadow:'0 6px 16px rgba(14,165,233,0.28)',
            }}
          >
            <FileDown style={{ width:16, height:16 }} />
            {tAdmin('report_button_logbook')}
          </motion.button>
        </div>

        {/* Idioma */}
        <LanguageSwitcher darkMode={darkMode} />

        {/* Notificaciones (A6.2 parte 2B) */}
        <NotificationPreferencesSection darkMode={darkMode} />

        {/* Seguridad */}
        <div style={{ borderRadius:18, background:cardBg, border, padding:'20px' }}>
          <SectionTitle darkMode={darkMode}>{t('security')}</SectionTitle>

          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:40, height:40, borderRadius:12, background:biometricEnabled?'rgba(22,163,74,0.1)':'rgba(100,116,139,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Fingerprint style={{ width:20, height:20, color:biometricEnabled?'#16a34a':'#64748b' }} />
              </div>
              <div>
                <p style={{ margin:0, fontSize:14, fontWeight:700, color:text }}>{t('biometric_login_label')}</p>
                <p style={{ margin:'2px 0 0', fontSize:12, color:sub }}>
                  {biometricEnabled ? t('biometric_active') : t('biometric_inactive')}
                </p>
              </div>
            </div>
            {biometricEnabled ? (
              <motion.button onClick={handleDisableBiometric}
                whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                style={{ padding:'8px 14px', borderRadius:10, border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.06)', color:'#ef4444', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                {t('disable')}
              </motion.button>
            ) : (
              <motion.button onClick={handleEnableBiometric}
                whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                style={{ padding:'8px 14px', borderRadius:10, border:'1px solid rgba(37,99,235,0.3)', background:'rgba(37,99,235,0.06)', color:'#2563eb', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                {t('activate')}
              </motion.button>
            )}
          </div>
        </div>

        {/* Miembro desde */}
        {currentUser?.created_at && (
          <p style={{ textAlign:'center', fontSize:12, color:darkMode?'rgba(255,255,255,0.2)':'#94a3b8' }}>
            {t('member_since')} {new Date(currentUser.created_at).toLocaleDateString(
              i18n.language === 'en' ? 'en-US' : 'es-AR',
              { year:'numeric', month:'long' }
            )}
          </p>
        )}
      </div>

      {/* Modal de descarga de logbook (sin user_id → backend usa req.user.id) */}
      <AnimatePresence>
        {logbookModalOpen && (
          <ExportReportModal
            isOpen
            title={tAdmin('report_my_logbook_modal_title')}
            formats={['pdf', 'xlsx']}
            periodOptions={{ monthPicker: true, rangePicker: true, allPicker: true }}
            onExport={(params) => reportsService.logbook(params)}
            onClose={() => setLogbookModalOpen(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {saved && (
          <motion.div
            key="profile-toast"
            initial={{ opacity:0, y:-24, scale:0.92 }}
            animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y:-16 }}
            transition={{ type:'spring', stiffness:200, damping:22 }}
            style={{
              position:'fixed', top:80, left:'50%', transform:'translateX(-50%)',
              zIndex:200, display:'flex', alignItems:'center', gap:10,
              padding:'12px 20px', borderRadius:20,
              background:'linear-gradient(135deg,#16a34a,#15803d)',
              boxShadow:'0 8px 24px rgba(22,163,74,0.35)',
              whiteSpace:'nowrap', pointerEvents:'none',
            }}>
            <CheckCircle style={{ width:16, height:16, color:'white', flexShrink:0 }} />
            <span style={{ fontSize:14, fontWeight:700, color:'white' }}>
              Cambios guardados correctamente
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </motion.div>
  );
};

export default MyProfile;
